import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../lib/api.js';
import { formatCurrency } from '../../../lib/formatters.js';

export type BankFormat = 'MANDIRI' | 'BCA' | 'BNI' | 'ISO20022_PAIN001';
type Method = 'BANK_TRANSFER' | 'INVOICE_NETTING';

interface SelectedClaim {
  id: string;
  claimNumber: string;
  approvedAmount: number | null;
  isoCurrencyCode: string;
  distributorName: string;
}

interface Props {
  selectedClaims: SelectedClaim[];
  onSuccess: (batchId: string) => void;
  onCancel: () => void;
}

const BANK_FORMATS: { value: BankFormat; label: string; description: string }[] = [
  { value: 'MANDIRI', label: 'Bank Mandiri Bulk Payment (CSV)', description: 'Format LLG/IBT — default untuk Indonesia' },
  { value: 'BCA', label: 'BCA KlikBCA Bisnis (CSV)', description: 'Format tanpa header, kode bank 3 digit' },
  { value: 'BNI', label: 'BNI Direct (CSV)', description: 'Format dengan header BNI' },
  { value: 'ISO20022_PAIN001', label: 'Generic ISO 20022 PAIN.001 (XML)', description: 'Stub — preview saja, belum production-grade' },
];

export function SettlementMethodPicker({ selectedClaims, onSuccess, onCancel }: Props) {
  const { t } = useTranslation();
  const [method, setMethod] = useState<Method>('BANK_TRANSFER');
  const [bankFormat, setBankFormat] = useState<BankFormat>('MANDIRI');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [claimErrors, setClaimErrors] = useState<{ claimNumber: string; reason: string }[]>([]);

  const totalByCurrency: Record<string, number> = {};
  for (const c of selectedClaims) {
    if (c.approvedAmount != null) {
      totalByCurrency[c.isoCurrencyCode] = (totalByCurrency[c.isoCurrencyCode] ?? 0) + c.approvedAmount;
    }
  }

  async function handleConfirm() {
    setSubmitting(true);
    setError(null);
    setClaimErrors([]);
    try {
      const body: Record<string, unknown> = {
        claimIds: selectedClaims.map((c) => c.id),
        method,
      };
      if (method === 'BANK_TRANSFER') body['bankFormat'] = bankFormat;

      const res = await api.post<{ batchId: string; downloadUrl: string | null }>('/settlement/batches', body);
      onSuccess(res.batchId);
    } catch (err) {
      const e = err as Error & { body?: Record<string, unknown> };
      const ceRaw = e.body?.['claimErrors'];
      if (Array.isArray(ceRaw) && ceRaw.length > 0) {
        setClaimErrors(ceRaw as { claimNumber: string; reason: string }[]);
        setError(t('settlement.batch.preflightFailed', { defaultValue: 'Some claims cannot be settled — see details below.' }));
      } else {
        setError(e.message ?? t('errors.internalServerError'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center p-4">
      <div className="bg-bone-soft border border-hairline rounded w-full max-w-lg shadow-xl">
        <div className="p-6 border-b border-hairline">
          <h2 className="font-serif italic text-xl text-ink">{t('settlement.picker.title')}</h2>
          <p className="text-sm text-slate-500 mt-1">
            {t('settlement.picker.subtitle', { count: selectedClaims.length })}
            {' · '}
            {Object.entries(totalByCurrency).map(([cur, amt]) => formatCurrency(amt, cur)).join(', ')}
          </p>
        </div>

        <div className="p-6 space-y-5">
          {/* Method */}
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t('settlement.picker.methodLabel')}</p>
            {(['BANK_TRANSFER', 'INVOICE_NETTING'] as Method[]).map((m) => (
              <label key={m} className={['flex items-start gap-3 p-3 border rounded cursor-pointer transition-colors', method === m ? 'border-copper bg-copper/5' : 'border-hairline bg-bone'].join(' ')}>
                <span className={['w-4 h-4 rounded-full border-2 mt-0.5 shrink-0 flex items-center justify-center', method === m ? 'border-copper bg-copper' : 'border-slate-400'].join(' ')}>
                  {method === m && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                </span>
                <input type="radio" className="sr-only" checked={method === m} onChange={() => setMethod(m)} />
                <div>
                  <p className="text-sm font-medium text-ink">{t(`settlement.picker.methods.${m}.label`)}</p>
                  <p className="text-xs text-slate-500">{t(`settlement.picker.methods.${m}.description`)}</p>
                </div>
              </label>
            ))}
          </div>

          {/* Bank format sub-selector */}
          {method === 'BANK_TRANSFER' && (
            <div className="space-y-2 ml-7">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t('settlement.picker.bankFormatLabel')}</p>
              {BANK_FORMATS.map((f) => (
                <label key={f.value} className={['flex items-start gap-3 p-2.5 border rounded cursor-pointer transition-colors', bankFormat === f.value ? 'border-copper bg-copper/5' : 'border-hairline bg-bone'].join(' ')}>
                  <span className={['w-3.5 h-3.5 rounded-full border-2 mt-0.5 shrink-0 flex items-center justify-center', bankFormat === f.value ? 'border-copper bg-copper' : 'border-slate-400'].join(' ')}>
                    {bankFormat === f.value && <span className="w-1 h-1 rounded-full bg-white" />}
                  </span>
                  <input type="radio" className="sr-only" checked={bankFormat === f.value} onChange={() => setBankFormat(f.value)} />
                  <div>
                    <p className="text-sm text-ink">{f.label}</p>
                    <p className="text-xs text-slate-500">{f.description}</p>
                  </div>
                </label>
              ))}
            </div>
          )}

          {/* Invoice netting note */}
          {method === 'INVOICE_NETTING' && (
            <div className="ml-7 p-3 bg-copper/5 border border-copper/20 rounded text-xs text-copper-deep">
              {t('settlement.picker.nettingNote')}
            </div>
          )}

          {error && <p className="text-sm text-danger">{error}</p>}
          {claimErrors.length > 0 && (
            <ul className="mt-1 space-y-1 max-h-40 overflow-y-auto">
              {claimErrors.map((ce, i) => (
                <li key={i} className="text-xs text-danger bg-danger/5 border border-danger/20 rounded px-2 py-1">
                  <span className="font-mono font-semibold">{ce.claimNumber}</span>
                  {' — '}
                  {ce.reason}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="p-6 border-t border-hairline flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-slate-600 hover:text-ink transition-colors"
            disabled={submitting}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className="px-5 py-2 bg-copper text-bone-soft rounded text-sm font-semibold hover:bg-copper-warm transition-colors disabled:opacity-50"
          >
            {submitting ? t('common.processing') : t('settlement.picker.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
